import { Link } from 'wouter'
import { Avatar } from '../../components/Avatar'
import { Card } from '../../components/Card'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../routes'
import { styled } from '../../setup'
import { borders, colors, spacing, typography } from '../../theme'

const Title = styled('h1')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize3xl};
  font-weight: ${typography.fontWeightBold};
  margin: 0 0 ${spacing['2xl']} 0;
`

const ChildGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${spacing.lg};
`

const ChildCard = styled('a')`
  background: ${colors.bgSurface};
  border: ${borders.widthMedium} solid ${colors.border};
  border-radius: ${borders.radiusLg};
  padding: ${spacing.lg};
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
  text-decoration: none;

  &:hover {
    background: ${colors.bgSurfaceAlt};
    border-color: ${colors.accentPrimary};
    box-shadow: 0 2px 8px ${colors.shadow};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.3);
  }
`

const ChildInfo = styled('div')`
  flex: 1;
  min-width: 0;
`

const ChildName = styled('div')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeLg};
  font-weight: ${typography.fontWeightMedium};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`

const ChildMeta = styled('div')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  margin-top: ${spacing.xs};
`

const Badge = styled('span')<{ variant: 'success' | 'warning' | 'info' }>`
  display: inline-block;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borders.radius};
  font-size: 12px;
  font-weight: ${typography.fontWeightMedium};
  text-transform: uppercase;
  letter-spacing: 0.03em;

  background: ${(props) =>
    ({
      success: colors.successBg,
      warning: 'rgba(251, 191, 36, 0.15)',
      info: 'rgba(59, 130, 246, 0.15)',
    })[props.variant]};

  color: ${(props) =>
    ({
      success: colors.success,
      warning: '#b45309',
      info: '#1d4ed8',
    })[props.variant]};
`

const EmptyState = styled('div')`
  text-align: center;
  padding: ${spacing['4xl']} ${spacing['2xl']};
`

const EmptyTitle = styled('h2')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeXl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.md} 0;
`

const EmptyText = styled('p')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeBase};
  margin: 0;
  line-height: 1.6;
`

function formatRole(role: string): string {
  switch (role) {
    case 'FULL_GUARDIAN':
      return 'Full Guardian'
    case 'LIMITED_GUARDIAN':
      return 'Limited Guardian'
    case 'VIEW_ONLY':
      return 'View Only'
    default:
      return role.replace(/_/g, ' ')
  }
}

function formatKind(kind: string): string {
  switch (kind) {
    case 'PARENT':
      return 'Parent'
    case 'STEPPARENT':
      return 'Stepparent'
    case 'GUARDIAN':
      return 'Guardian'
    case 'SIBLING':
      return 'Sibling'
    case 'OTHER':
      return 'Other'
    default:
      return kind
  }
}

export function DashboardPage() {
  const { managedChildren } = useAuth()

  return (
    <>
      <Title>Your Children</Title>

      {managedChildren.value.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyTitle>No linked children yet</EmptyTitle>
            <EmptyText>
              Children can send you a link request from their account. Once they enter your email,
              you'll receive a link to approve the connection.
            </EmptyText>
          </EmptyState>
        </Card>
      ) : (
        <ChildGrid>
          {managedChildren.value.map((child) => (
            <Link key={child.id} href={ROUTES.GUARDIAN_CHILD.replace(':childId', child.id)}>
              <ChildCard>
                <Avatar name={child.displayName} size={56} />
                <ChildInfo>
                  <ChildName>
                    {child.displayName}
                    <Badge variant="info">{formatKind(child.guardianKind)}</Badge>
                  </ChildName>
                  <ChildMeta>{formatRole(child.accessRole)}</ChildMeta>
                </ChildInfo>
              </ChildCard>
            </Link>
          ))}
        </ChildGrid>
      )}
    </>
  )
}
